import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaDotCircle } from '@react-icons/all-files/fa/FaDotCircle';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { EditButton, RenderActions } from '../../../../commons/DataGridActions';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities } from '../../../../manager/workflow-nodo-instancia.reducer';
import UserInfo from '../../../../commons/UserInfo';
import LinkCell from '../../../../commons/LinkCell';
import BooleanCell from '../../../../commons/BooleanCell';

const BASE = '/configuracion/workflow/instancias-nodos';

// ── Estado badge (reused from instancias-workflow pattern) ────────────────────

const ESTADO_CFG: Record<string, { label: string; bg: string; color: string }> = {
  PENDIENTE: { label: 'Pendiente', bg: '#fefce8', color: '#a16207' },
  GENERADO: { label: 'Generado', bg: '#eff6ff', color: '#1d4ed8' },
  EJECUCION: { label: 'En Ejecución', bg: '#f0fdf4', color: '#15803d' },
  FINALIZADO: { label: 'Finalizado', bg: '#f1f5f9', color: '#0f172a' },
  CANCELADO: { label: 'Cancelado', bg: '#fef2f2', color: '#b91c1c' },
};

function EstadoBadge({ estado }: { estado?: string }) {
  if (!estado) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  const cfg = ESTADO_CFG[estado.toUpperCase()] ?? { label: estado, bg: '#f1f5f9', color: '#475569' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color,
      letterSpacing: '0.04em', whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function fmtDate(val?: string) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtSeconds(secs?: number) {
  if (secs == null) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function InstanciasNodosPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => (s as any).workflowNodoInstancia.entities);
  const totalItems = useAppSelector(s => (s as any).workflowNodoInstancia.totalItems);
  const loading = useAppSelector(s => (s as any).workflowNodoInstancia.loading);

  const [rows, setRows] = useState<any[]>([]);
  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => dispatch(getEntities(params));

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.4 },
    {
      field: 'workflowInstancia.workflowEmpresa.empresa.rfc',
      headerName: 'RFC',
      flex: 2,
      renderCell: (p: any) => p.row.workflowInstancia?.workflowEmpresa?.empresa?.rfc ?? '—',
    },
    {
      field: 'workflowInstancia.workflowEmpresa.empresa.razonSocial',
      headerName: 'Razón Social / Grupo',
      flex: 3.5,
      renderCell: (p: any) => <>
        <div>
          <div>{p.row.workflowInstancia?.workflowEmpresa?.empresa?.razonSocial ?? '—'}</div>
          <div>{p.row.workflowInstancia?.workflowEmpresa?.empresa?.grupoEmpresarial?.alias}</div>
        </div>
      </>,
    },
    {
      field: 'workflowInstancia.workflowEmpresa.workflow.clase?.nombre',
      headerName: 'Workflow Clase',
      flex: 2,
      renderCell: (p: any) => (
        <>
          <div>
            <div>
              <LinkCell
                label={p.row.workflowInstancia?.workflowEmpresa?.workflow?.nombre}
                filterName="workflowInstancia.workflowEmpresa.workflow.id"
                filterValue={p.row.workflowInstancia?.workflowEmpresa?.workflow?.id}
              />
            </div>
            <div>
              <LinkCell
                label={p.row.workflowInstancia?.workflowEmpresa?.workflow?.workflowClase?.nombre}
                filterName="workflowInstancia.workflowEmpresa.workflow.workflowClase.nombre"
                filterValue={p.row.workflowInstancia?.workflowEmpresa?.workflow?.workflowClase?.id}
              />
            </div>
          </div>
        </>
      ),
    },
    {
      field: 'workflowNodo.tipoNodo',
      headerName: 'Nodo',
      flex: 1.3,
      renderCell: (p: any) =>
        <>
          <EstadoBadge estado={p.row.workflowNodo?.profaseActividad?.descripcion ?? '[' + p.row.workflowNodo?.tipoNodo + ']'} />
        </>

    },
    {
      field: 'workflowInstancia.estado',
      headerName: 'Estado WF',
      flex: 1.3,
      renderCell: (p: any) => <EstadoBadge estado={p.row.workflowInstancia?.estado} />,
    },
    {
      field: 'estado',
      headerName: 'Estado Nodo',
      flex: 1.3,
      renderCell: (p: any) => <EstadoBadge estado={p.row.estado} />,
    },
    {
      field: 'resultado',
      headerName: 'Resultado',
      flex: 1.2,
      renderCell: (p: any) => p.row.resultado ?? '—',
    },
    {
      field: 'fechaInicio',
      headerName: 'Fecha Inicio',
      flex: 1.3,
      renderCell: (p: any) => fmtDate(p.row.fechaInicio),
    },
    {
      field: 'fechaAutorizacion',
      headerName: 'Fecha Autor.',
      flex: 1.3,
      renderCell: (p: any) => fmtDate(p.row.fechaAutorizacion),
    },
    {
      field: 'tiempoTrabajoSegundos',
      headerName: 'Tiempo',
      flex: 1,
      renderCell: (p: any) => fmtSeconds(p.row.tiempoTrabajoSegundos),
    },
    {
      field: 'ignorar',
      headerName: 'Habilitado',
      flex: 0.8,
      renderCell: (p: any) => <BooleanCell value={!p.row.ignorar} />,
    },
    {
      field: 'usuarioAsignado',
      headerName: 'Usuario',
      flex: 2,
      renderCell: (p: any) => <UserInfo usuario={p.row.usuarioAsignado} size={22} />,
    },
    {
      field: 'usuarioAutoriza',
      headerName: 'Autoriza',
      flex: 2,
      renderCell: (p: any) => <UserInfo usuario={p.row.usuarioAutoriza} size={22} />,
    },
    {
      field: '__actions',
      headerName: '',
      flex: 0.4,
      renderCell: (p: any) => (
        <RenderActions>
          <EditButton onClick={() => navigate(`${BASE}/${p.row.id}/edit`)} />
        </RenderActions>
      ),
    },
  ];

  const filters = [
    { label: 'Nodo', name: 'workflowNodo.tipoNodo' },
    { label: 'Workflow Instancia ID', name: 'workflowInstancia.id' },
    { label: 'RFC', name: 'workflowInstancia.workflowEmpresa.empresa.rfc' },
    { label: 'Razón Social', name: 'workflowInstancia.workflowEmpresa.empresa.razonSocial' },
    { label: 'Workflow ID', name: 'workflowInstancia.workflowEmpresa.workflow.id' },
    {
      label: 'Estado Nodo',
      name: 'estado',
      type: 'select',
      options: [
        { value: 'PENDIENTE', label: 'Pendiente' },
        { value: 'GENERADO', label: 'Generado' },
        { value: 'EJECUCION', label: 'En Ejecución' },
        { value: 'FINALIZADO', label: 'Finalizado' },
        { value: 'CANCELADO', label: 'Cancelado' },
      ],
    },
    {
      label: 'Estado WF',
      name: 'workflowInstancia.estado',
      type: 'select',
      options: [
        { value: 'GENERADO', label: 'Generado' },
        { value: 'EJECUCION', label: 'En Ejecución' },
        { value: 'FINALIZADO', label: 'Finalizado' },
      ],
    },
    {
      label: 'Workflow Clase',
      name: 'workflowInstancia.workflowEmpresa.workflow.workflowClase.nombre'
    }
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Instancias de Nodos', icon: <FaDotCircle /> },
      ]} />

      <PageHeader
        icon={<FaDotCircle />}
        iconColor="#ef4444"
        title="Instancias de Nodos"
        subtitle="Consulta y edición de instancias de nodos de workflow"
      />

      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
        getRowClassName={(params: any) => params.row.ignorar ? 'row-ignorada' : ''}
        sx={{
          '& .row-ignorada': {
            opacity: 0.5,
            color: '#94a3b8',
          },
          '& .row-ignorada .MuiDataGrid-cell': {
            color: '#94a3b8',
          },
        }}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
